import { currentLanguage, localeTag } from '@/i18n/format';
import React, { useState, useCallback, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Settings, LogOut, Minus, Sun, Moon, Users, ArrowRightLeft } from 'lucide-react';
import { LanguagePicker } from '../common/LanguagePicker';
import { useQuery } from '@tanstack/react-query';
import { currentTenant, usesUms } from '@/auth/umsSession';
import ums from '@/services/ums.service';
import { useTheme } from '@/themes';
import { MapView } from '../map/MapView';
import { MapControls } from '../map/MapControls';
import { MapTypeSelector } from '../map/MapTypeSelector';
import { LayerPanel } from '../Layers/LayerPanel';
import { MapLegend } from '../Legend/MapLegend';
import { AddressSearchBar } from '../Geocoder/AddressSearchBar';
import {
  FeatureInfoPanel,
  FeatureInfoResult,
  CatchmentSelection,
  CatchmentTimes,
  CatchmentOptions,
} from '../Info/FeatureInfoPanel';
import { CatchmentResultPanel } from '../Info/CatchmentResultPanel';
import { driveTravelType as driveTravelType_, routingPayload } from '../Info/RoutingControls';
import { ReportStudio } from '../ReportStudio/ReportStudio';
import { AnalysisPanel } from '../ToolPanels/AnalysisPanel';
import { DistancePanel } from '../ToolPanels/DistancePanel';
import { AddFeaturePanel } from '../ToolPanels/AddFeaturePanel';
import { AreaAnalysisPanel } from '../ToolPanels/AreaAnalysisPanel';
import { EditStylePanel } from '../ToolPanels/EditStylePanel';
import { StreetViewPanel } from '../ToolPanels/StreetViewPanel';
import { MultiInfoPanel } from '../ToolPanels/MultiInfoPanel';
import { PolyInfoPanel } from '../ToolPanels/PolyInfoPanel';
import { CannibalizationPanel } from '../ToolPanels/CannibalizationPanel';
import { FilterPanel } from '../ToolPanels/FilterPanel';
import { EditingHost } from '../editing/EditingHost';
import { setInteractionCallbacks, updateCannibMarkers, updateAnalysisMarker, clearCannibMarkers, clearDispHighlights, updateDistanceMarkers, clearDistanceMarkers } from '../../hooks/useMapInteractions';
import { useMapStore } from '../../store/map.store';
import { useAuth } from '../Auth/AuthProvider';
import apiClient from '../../services/api';
import {
  drawCatchmentPolygons,
  clearCatchmentPolygons,
  type ParsedPointData,
} from '../../utils/catchmentRenderer';
import { Coordinate } from 'ol/coordinate';
import TileWMS from 'ol/source/TileWMS';
import ImageWMS from 'ol/source/ImageWMS';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom';
import { useMapInteractions } from '../../hooks/useMapInteractions';

interface AppLayoutProps {
  children?: React.ReactNode;
}

/* Tools that need point selection */
const POINT_TOOLS = new Set(['catchment', 'ringBuffer', 'cannibalization', 'addPoint', 'distance']);

/* Cosmos layer list matching legacy code */
const COSMOS_LAYERS = [
  'Bim', 'A101', 'Carrefour', 'Kipa', 'Sok', 'Local_Stores',
  'Migros_Geocode', 'File', 'Profilo', 'Arcelik', 'Bosch',
  'Indesit', 'Sony', 'Vatan_Computer', 'Vestel', 'Teknosa',
  'Gratis', 'Flormar', 'Watsons', 'Rossmann', 'Eve', 'Golden_Rose',
];

/** Derive 1-2 letter initials from user info: name > email > login > '?' */
function getUserInitials(user: any): string {
  if (user?.firstName || user?.lastName) {
    const f = (user.firstName || '')[0] || '';
    const l = (user.lastName || '')[0] || '';
    return (f + l).toUpperCase() || '?';
  }
  if (user?.email) {
    return user.email[0].toUpperCase();
  }
  if (user?.login) {
    return user.login[0].toUpperCase();
  }
  return '?';
}

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout, hasAuthority, ums: signsInWithUms, changeCompany } = useAuth();
  const { theme, setTheme, palettes } = useTheme();
  const isDark = palettes[theme]?.mode === 'dark';
  const isAdmin = hasAuthority('ROLE_ADMIN');
  // The Users screen: whoever may manage the company's people in UMS (its administrator).
  const { data: umsAccess } = useQuery({
    queryKey: ['umsAuthorization', 'ums', currentTenant()],
    queryFn: async () => ((await usesUms()) ? ums.authorization('ums') : null),
    enabled: !!user,
    retry: false,
    staleTime: 5 * 60_000,
  });
  const canManageUsers = !!umsAccess?.screens?.['ums.users'];
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const [layerPanelOpen, setLayerPanelOpen] = useState(false);
  const [_showLegend] = useState(true);
  const currentTool = useMapStore((s) => s.currentTool);
  const { drawDistanceLine, calculateGeodesicDistance } = useMapInteractions();

  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  /* ── Feature Info state ── */
  const [infoResults, setInfoResults] = useState<FeatureInfoResult[] | null>(null);

  /* ── Analysis/point selection state ── */
  const [selectedPoint, setSelectedPoint] = useState<[number, number] | null>(null);

  /* ── Distance state ── */
  const [distStartPoint, setDistStartPoint] = useState<[number, number] | null>(null);
  const [distEndPoint, setDistEndPoint] = useState<[number, number] | null>(null);
  const [distSelectingPoint, setDistSelectingPoint] = useState<'start' | 'end' | null>(null);
  const [distResults, setDistResults] = useState<any>(null);

  /* ── Add Point / Add Polygon / Add Line state ── */
  const [addPointFeature, setAddPointFeature] = useState<Feature<Geometry> | null>(null);
  const [addPolygonFeature, setAddPolygonFeature] = useState<Feature<Geometry> | null>(null);
  const [addLineFeature, setAddLineFeature] = useState<Feature<Geometry> | null>(null);

  /* ── Area Analysis drawn polygon ── */
  const [areaAnalysisFeature, setAreaAnalysisFeature] = useState<Feature<Geometry> | null>(null);

  /* ── Poly Info click state ── */
  const [polyInfoClickResults, setPolyInfoClickResults] = useState<FeatureInfoResult[] | null>(null);
  const [polyInfoClickExtent, setPolyInfoClickExtent] = useState<[number, number, number, number] | null>(null);

  /* ── Multi Info accumulated results ── */
  const [multiInfoResults, setMultiInfoResults] = useState<FeatureInfoResult[]>([]);

  /* ── Street View point ── */
  const [streetViewPoint, setStreetViewPoint] = useState<[number, number] | null>(null);

  /* ── Cannibalization two-point selection ── */
  const [cannibPoint1, setCannibPoint1] = useState<[number, number] | null>(null);
  const [cannibPoint2, setCannibPoint2] = useState<[number, number] | null>(null);
  const [cannibSelectingPoint, setCannibSelectingPoint] = useState<1 | 2>(1);

  /* ── Catchment loading + result + error state ── */
  const [catchmentLoading, setCatchmentLoading] = useState(false);
  const [catchmentResults, setCatchmentResults] = useState<ParsedPointData[] | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [resultPanelMinimized, setResultPanelMinimized] = useState(false);

  /* ── Register interaction callbacks ── */
  const handleFeatureInfo = useCallback((results: FeatureInfoResult[]) => {
    const tool = useMapStore.getState().currentTool;

    if (tool === 'multiInfo') {
      // Accumulate: merge new results into existing by layer
      if (results.length > 0) {
        setMultiInfoResults((prev) => {
          let merged = [...prev];
          results.forEach((newLr) => {
            const existingIdx = merged.findIndex((m) => m.layerName === newLr.layerName);
            if (existingIdx >= 0) {
              const existing = merged[existingIdx];
              const existingIds = new Set(existing.features.map((f) => f.id));
              const newFeats = newLr.features.filter((f) => !existingIds.has(f.id));
              // Create a new object so React detects the change
              merged = [
                ...merged.slice(0, existingIdx),
                { ...existing, features: [...existing.features, ...newFeats] },
                ...merged.slice(existingIdx + 1),
              ];
            } else {
              merged = [...merged, { ...newLr }];
            }
          });
          return merged;
        });
      }
    } else if (results.length > 0) {
      setInfoResults(results);
    }
  }, []);

  const handlePointSelected = useCallback((_coord: Coordinate, lonLat: [number, number], feature?: Feature<Geometry>) => {
    const tool = useMapStore.getState().currentTool;

    if (tool === 'distance') {
      setDistSelectingPoint((prev) => {
        if (prev === 'start' || prev === null) {
          setDistStartPoint(lonLat);
          setDistResults(null);
          return 'end';
        } else {
          setDistEndPoint(lonLat);
          return 'start';
        }
      });
    } else if (tool === 'streetView') {
      setStreetViewPoint(lonLat);
    } else if (tool === 'addPoint') {
      setSelectedPoint(lonLat);
      if (feature) setAddPointFeature(feature);
    } else if (tool === 'cannibalization') {
      // Sequential two-point selection
      setCannibSelectingPoint((prev) => {
        if (prev === 1) {
          setCannibPoint1(lonLat);
          return 2;
        } else {
          setCannibPoint2(lonLat);
          return 1; // cycle back — user can re-select
        }
      });
    } else if (POINT_TOOLS.has(tool || '')) {
      setSelectedPoint(lonLat);
    }
  }, []);

  /* ── Polygon/Line drawn callback ── */
  const handlePolygonDrawn = useCallback((feature: Feature<Geometry>) => {
    const tool = useMapStore.getState().currentTool;
    if (tool === 'addPolygon') {
      setAddPolygonFeature(feature);
    } else if (tool === 'addLine') {
      setAddLineFeature(feature);
    } else if (tool === 'areaAnalysis') {
      // Clear first, then set in next tick to ensure reference change triggers useEffect
      setAreaAnalysisFeature(null);
      setTimeout(() => setAreaAnalysisFeature(feature), 0);
    }
  }, []);

  /* ── PolyInfo click callback ── */
  const handlePolyInfoClick = useCallback((results: FeatureInfoResult[], extent: [number, number, number, number]) => {
    setPolyInfoClickResults(results);
    setPolyInfoClickExtent(extent);
  }, []);

  React.useEffect(() => {
    setInteractionCallbacks({
      onFeatureInfo: handleFeatureInfo,
      onPointSelected: handlePointSelected,
      onPolygonDrawn: handlePolygonDrawn,
      onPolyInfoClick: handlePolyInfoClick,
    });
  }, [handleFeatureInfo, handlePointSelected, handlePolygonDrawn, handlePolyInfoClick]);

  /* Update analysis marker on the map for catchment/ringBuffer */
  React.useEffect(() => {
    const mapInst = useMapStore.getState().mapInstance;
    if (!mapInst) return;
    if (currentTool === 'catchment' || currentTool === 'ringBuffer') {
      updateAnalysisMarker(mapInst, selectedPoint, t('distance.pin_point'));
    }
  }, [selectedPoint, currentTool]);

  /* Update cannibalization markers on the map whenever points change */
  React.useEffect(() => {
    const mapInst = useMapStore.getState().mapInstance;
    if (!mapInst || currentTool !== 'cannibalization') return;
    updateCannibMarkers(mapInst, cannibPoint1, cannibPoint2);
  }, [cannibPoint1, cannibPoint2, currentTool]);

  /* Update distance markers on the map whenever start/end points change */
  React.useEffect(() => {
    const mapInst = useMapStore.getState().mapInstance;
    if (!mapInst || currentTool !== 'distance') return;
    updateDistanceMarkers(mapInst, distStartPoint, distEndPoint);
  }, [distStartPoint, distEndPoint, currentTool]);

  /* Reset state when tool changes */
  const prevToolRef = React.useRef(currentTool);
  React.useEffect(() => {
    const prevTool = prevToolRef.current;
    prevToolRef.current = currentTool;

    const mapInst = useMapStore.getState().mapInstance;

    /* Reset cannibalization state */
    if (currentTool === 'cannibalization') {
      setCannibPoint1(null);
      setCannibPoint2(null);
      setCannibSelectingPoint(1);
    } else {
      // Clear cannibalization markers when switching away
      if (mapInst) clearCannibMarkers(mapInst);
    }

    /* Reset selectedPoint and analysis markers when switching between analysis tools */
    const analysisTools = new Set(['catchment', 'ringBuffer', 'cannibalization']);
    if (analysisTools.has(currentTool || '') && prevTool !== currentTool) {
      // Clear selectedPoint and analysis pin when switching between any analysis tools
      setSelectedPoint(null);
      if (mapInst) {
        updateAnalysisMarker(mapInst, null);
        clearCannibMarkers(mapInst);
      }
      // Clear error state from previous analysis
      setAnalysisError(null);
    }

    /* Clear multiInfo highlights when switching away from multiInfo */
    if (prevTool === 'multiInfo' && currentTool !== 'multiInfo') {
      setMultiInfoResults([]);
      clearDispHighlights();
    }

    /* Clear distance markers and lines when switching away from distance */
    if (prevTool === 'distance' && currentTool !== 'distance') {
      if (mapInst) {
        clearDistanceMarkers(mapInst);
        clearDispHighlights();
      }
    }

    if (currentTool === 'distance') {
      setDistSelectingPoint('start');
      setDistStartPoint(null);
      setDistEndPoint(null);
      setDistResults(null);
    }
    /* Geocoder tool — focus the address search bar */
    if (currentTool === 'geocoder') {
      setTimeout(() => {
        const searchInput = document.querySelector<HTMLInputElement>('[data-geocoder-input], .address-search-input, input[placeholder*="Search"], input[placeholder*="Address"]');
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
      }, 100);
    }
  }, [currentTool]);

  /* ── Distance calculation ── */
  const handleCalculateDistance = useCallback(
    async (modes: { walk: boolean; drive: boolean; bird: boolean }) => {
      if (!distStartPoint || !distEndPoint) return;

      const results: any = {};

      // Bird's eye: local geodesic calculation
      if (modes.bird) {
        const distMeters = calculateGeodesicDistance(distStartPoint, distEndPoint);
        const formatDist = (m: number) => m > 1000
          ? `${(m / 1000).toLocaleString(localeTag(), { maximumFractionDigits: 2, minimumFractionDigits: 2 })} km`
          : `${Math.round(m).toLocaleString(localeTag())} m`;
        results.birdDistance = formatDist(distMeters);
      }

      // Draw bird line immediately while waiting for API
      drawDistanceLine(distStartPoint, distEndPoint, modes);
      setDistResults({ ...results });

      const origin = `${distStartPoint[1]},${distStartPoint[0]}`; // lat,lng
      const destination = `${distEndPoint[1]},${distEndPoint[0]}`;
      const routes: { walkCoords?: [number, number][]; driveCoords?: [number, number][] } = {};

      // Fetch actual routes from Google Directions API via backend proxy
      const fetchRoute = async (mode: 'walking' | 'driving') => {
        try {
          const resp = await apiClient.get('/directions', {
            params: { origin, destination, mode, language: currentLanguage() },
          });
          const data = resp.data;
          if (data.status === 'OK' && data.routes?.[0]) {
            const route = data.routes[0];
            const leg = route.legs?.[0];
            const polyline = route.overview_polyline?.points;
            return {
              distance: leg?.distance?.text || '',
              duration: leg?.duration?.text || '',
              polyline: polyline || '',
            };
          }
        } catch (err) {
          console.error(`Directions API (${mode}) error:`, err);
        }
        return null;
      };

      // Fetch walking and driving routes in parallel
      const [walkResult, driveResult] = await Promise.all([
        modes.walk ? fetchRoute('walking') : Promise.resolve(null),
        modes.drive ? fetchRoute('driving') : Promise.resolve(null),
      ]);

      if (walkResult) {
        results.walkDistance = walkResult.distance;
        results.walkTime = walkResult.duration;
        if (walkResult.polyline) {
          const { decodePolyline } = await import('../../utils/polylineDecode');
          routes.walkCoords = decodePolyline(walkResult.polyline);
        }
      }

      if (driveResult) {
        results.driveDistance = driveResult.distance;
        results.driveTime = driveResult.duration;
        if (driveResult.polyline) {
          const { decodePolyline } = await import('../../utils/polylineDecode');
          routes.driveCoords = decodePolyline(driveResult.polyline);
        }
      }

      // Redraw with actual route polylines
      drawDistanceLine(distStartPoint, distEndPoint, modes, routes);
      setDistResults({ ...results });
    },
    [distStartPoint, distEndPoint, drawDistanceLine, calculateGeodesicDistance],
  );

  /* ── Close tool panels ── */
  const handleCloseToolPanel = useCallback(() => {
    setSelectedPoint(null);
    setInfoResults(null);
    setDistStartPoint(null);
    setDistEndPoint(null);
    setDistSelectingPoint(null);
    setDistResults(null);
    setAddPointFeature(null);
    setAddPolygonFeature(null);
    setAddLineFeature(null);
    setAreaAnalysisFeature(null);
    setPolyInfoClickResults(null);
    setPolyInfoClickExtent(null);
    setMultiInfoResults([]);
    clearDispHighlights();
    setStreetViewPoint(null);
    setCannibPoint1(null);
    setCannibPoint2(null);
    setCannibSelectingPoint(1);
    useMapStore.getState().setCurrentTool(null);
  }, []);

  /* ══════════════════════════════════════════════════════════════
     Build layers array based on layerControl option (legacy logic)
     ══════════════════════════════════════════════════════════════ */
  const buildLayers = useCallback(
    (layerControl: string): { layerName: string; isAnalysis: number }[] => {
      switch (layerControl) {
        case 'nbHood':
          return [{ layerName: 'nbHood', isAnalysis: 1 }];

        case 'cosmos':
          return COSMOS_LAYERS.map((name) => ({ layerName: name, isAnalysis: 0 }));

        case 'allVisible':
        default: {
          const mapInst = useMapStore.getState().mapInstance;
          if (!mapInst) return [{ layerName: 'nbHood', isAnalysis: 1 }];
          const visible: { layerName: string; isAnalysis: number }[] = [];
          mapInst.getLayers().forEach((layer: any) => {
            if (!layer.getVisible()) return;
            const src = layer.getSource?.();
            if (src && (src instanceof TileWMS || src instanceof ImageWMS)) {
              // The server knows our layers by GeoServer's name, not by the label the map shows.
              const name = layer.get('layerName');
              if (name) visible.push({ layerName: name, isAnalysis: 0 });
            } else if (layer.get('companyLayer')) {
              // A company's own layer: asked for as co:<key> (docs/en/60 sec.10).
              visible.push({ layerName: layer.get('name'), isAnalysis: 0 });
            }
          });
          return visible.length > 0 ? visible : [{ layerName: 'nbHood', isAnalysis: 1 }];
        }
      }
    }, [],
  );

  /* ══════════════════════════════════════════════════════════════
     Start catchment from info panel
     ══════════════════════════════════════════════════════════════ */
  const handleStartCatchment = useCallback(
    async (
      selections: CatchmentSelection[],
      times: CatchmentTimes,
      options: CatchmentOptions,
    ) => {
      setCatchmentLoading(true);
      setAnalysisError(null);

      try {
        /* The drive's travel type from the traffic: 2 normal, 4 heavy, 7 very heavy, 6 none */
        const driveTravelType = driveTravelType_(options.trafficControl);

        /* The places, as latitude and longitude (Veramenta docs/en/60 sec.7). The old server read a ring's
         * centre the other way round and this swapped them for a ring; the server no longer does either. */
        const wnDStartData = selections.map((sel) => {
          const travellers: { time: number[]; travelType: number }[] = [];

          if (sel.walkin) {
            travellers.push({ time: times.walkin, travelType: 1 });
          }
          if (sel.drive) {
            travellers.push({ time: times.drive, travelType: driveTravelType });
          }
          if (sel.ringBuffer) {
            travellers.push({ time: times.ring, travelType: 3 });
          }
          if (sel.motor) {
            travellers.push({ time: times.motor, travelType: 5 });
          }

          return {
            name: sel.name,
            lat: sel.lat,
            lon: sel.lon,
            traveller: travellers,
          };
        });

        // Ring buffer only needs nbHood — skip shopping/market/address layers
        const isRingBufferAnalysis = wnDStartData.every(
          (d: any) => d.traveller?.length === 1 && d.traveller[0].travelType === 3,
        );
        const layers = isRingBufferAnalysis
          ? [{ layerName: 'nbHood', isAnalysis: 1 }]
          : buildLayers(options.layerControl);

        const payload = {
          analysisType: 1,       // 1 = single-point catchment
          softRoleId: user?.softRole?.id ?? 1,
          layers,
          wnDStartData,
          ...(routingPayload(options.routing) ? { routing: routingPayload(options.routing) } : {}),
        };

        console.log('Catchment payload:', JSON.stringify(payload, null, 2));

        const response = await apiClient.put('/getDriveTimeReport', payload, {
          timeout: 600000, // 10 min — catchment with large time bands can be slow
        });
        const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;

        console.log('Catchment response:', data);

        if (data.StatusRaw === 1) {
          console.error('Catchment service error:', data.Status);
          setAnalysisError(t('errors.analysis_error', { title: t('analysis.catchment'), reason: data.Status || t('errors.server_error') }));
          return;
        }

        /* Draw polygons on the map and parse the result data */
        const mapInst = useMapStore.getState().mapInstance;
        if (mapInst) {
          // Clear any previous catchment polygons
          clearCatchmentPolygons(mapInst);
          const parsed = drawCatchmentPolygons(mapInst, data);
          setCatchmentResults(parsed);
        }

        /* StatusRaw=2 means partial success (credit/role issue) — show warning but keep results */
        if (data.StatusRaw === 2) {
          setAnalysisError(data.Status || t('errors.analysis_partial'));
        }
      } catch (err: any) {
        const msg = err?.response?.data?.message || err?.response?.data?.Status || err?.message || t('errors.unknown');
        console.error('Catchment request failed:', msg, err);
        setAnalysisError(t('errors.analysis_failed', { title: t('analysis.catchment'), reason: msg }));
      } finally {
        setCatchmentLoading(false);
      }
    },
    [buildLayers],
  );

  /* ══════════════════════════════════════════════════════════════
     Start cannibalization (analysisType = 2)
     ══════════════════════════════════════════════════════════════ */
  const handleStartCannibalization = useCallback(
    async (
      selections: CatchmentSelection[],
      times: CatchmentTimes,
      options: CatchmentOptions,
      _analysisName: string,
    ) => {
      setCatchmentLoading(true);
      setAnalysisError(null);

      try {
        const driveTravelType = driveTravelType_(options.trafficControl);

        const wnDStartData = selections.map((sel) => {
          const travellers: { time: number[]; travelType: number }[] = [];
          if (sel.walkin) travellers.push({ time: times.walkin, travelType: 1 });
          if (sel.drive) travellers.push({ time: times.drive, travelType: driveTravelType });
          if (sel.ringBuffer) travellers.push({ time: times.ring, travelType: 3 });
          if (sel.motor) travellers.push({ time: times.motor, travelType: 5 });
          return { name: sel.name, lat: sel.lat, lon: sel.lon, traveller: travellers };
        });

        const layers = buildLayers(options.layerControl);

        const payload = {
          analysisType: 2,       // 2 = cannibalization (multi-point)
          softRoleId: user?.softRole?.id ?? 1,
          layers,
          wnDStartData,
          ...(routingPayload(options.routing) ? { routing: routingPayload(options.routing) } : {}),
        };

        console.log('Cannibalization payload:', JSON.stringify(payload, null, 2));

        const response = await apiClient.put('/getDriveTimeReport', payload, {
          timeout: 600000,
        });
        const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;

        console.log('Cannibalization response:', data);

        if (data.StatusRaw === 1) {
          console.error('Cannibalization service error:', data.Status);
          setAnalysisError(t('errors.analysis_error', { title: t('cannib.title'), reason: data.Status || t('errors.server_error') }));
          return;
        }

        const mapInst = useMapStore.getState().mapInstance;
        if (mapInst) {
          clearCatchmentPolygons(mapInst);
          const parsed = drawCatchmentPolygons(mapInst, data);
          setCatchmentResults(parsed);

          if (parsed.length === 0) {
            console.warn('Cannibalization: drawCatchmentPolygons returned empty results', data);
            setAnalysisError(t('errors.cannib_empty'));
          }
        }

      } catch (err: any) {
        const msg = err?.response?.data?.message || err?.response?.data?.Status || err?.message || t('errors.unknown');
        console.error('Cannibalization request failed:', msg, err);
        setAnalysisError(t('errors.analysis_failed', { title: t('cannib.title'), reason: msg }));
      } finally {
        setCatchmentLoading(false);
      }
    },
    [buildLayers],
  );

  /* ── Catchment result clear/close handlers ── */
  const handleCloseCatchmentResults = useCallback(() => {
    setShowCloseConfirm(true);
  }, []);

  const handleConfirmCloseCatchment = useCallback(() => {
    const mapInst = useMapStore.getState().mapInstance;
    if (mapInst) clearCatchmentPolygons(mapInst);
    setCatchmentResults(null);
    setShowCloseConfirm(false);
    setResultPanelMinimized(false);
  }, []);

  const handleClearCatchmentResults = useCallback(() => {
    const mapInst = useMapStore.getState().mapInstance;
    if (mapInst) clearCatchmentPolygons(mapInst);
    setCatchmentResults(null);
    setShowCloseConfirm(false);
    setResultPanelMinimized(false);
  }, []);

  const showInfoPanel = infoResults && infoResults.length > 0;
  const showAnalysisPanel = currentTool === 'catchment' || currentTool === 'ringBuffer';
  const showCannibalizationPanel = currentTool === 'cannibalization';
  const showDistancePanel = currentTool === 'distance';
  const showAddPointPanel = currentTool === 'addPoint';
  const showAddPolygonPanel = currentTool === 'addPolygon';
  const showAddLinePanel = currentTool === 'addLine';
  const showAreaAnalysisPanel = areaAnalysisFeature !== null;
  const showEditStylePanel = currentTool === 'editStyle';
  const showStreetViewPanel = currentTool === 'streetView';
  const showMultiInfoPanel = currentTool === 'multiInfo';
  const showPolyInfoPanel = currentTool === 'polyInfo';
  const showFilterPanel = currentTool === 'filter';

  return (
    <div className="relative w-screen h-screen overflow-hidden bg-gn-primary">
      {/* Map Container */}
      <div className="absolute inset-0 z-0">
        <Suspense
          fallback={
            <div className="w-full h-full flex items-center justify-center bg-gn-primary">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gn-blue mx-auto mb-4" />
                <p className="text-gn-secondary text-sm">{t('map.loading')}</p>
              </div>
            </div>
          }
        >
          <MapView />
        </Suspense>
      </div>

      {/* UI Overlays */}

      {/* Top Center Toolbar */}
      <MapControls />

      {/* Layer Panel */}
      <LayerPanel
        isOpen={layerPanelOpen}
        onClose={() => setLayerPanelOpen(false)}
        onToggle={() => setLayerPanelOpen(!layerPanelOpen)}
      />

      {/* Map Type Selector */}
      <MapTypeSelector />

      {/* ─── Top-right: Search + Language + User ─── */}
      <div className="fixed top-3 right-3 z-50 flex items-center gap-1.5">
        {/* Search bar */}
        <AddressSearchBar />

        {/* Language */}
        <LanguagePicker />

        {/* User button (initials) + dropdown */}
        <div className="relative">
          <button
            onClick={() => setUserMenuOpen((v) => !v)}
            className="flex items-center justify-center w-9 h-9 rounded-full text-[11px] font-bold text-gn-primary bg-gn-secondary hover:bg-gn-tertiary shadow-lg transition-colors"
            title={user?.login || t('menu.account')}
          >
            {getUserInitials(user)}
          </button>

          {userMenuOpen && (
            <>
              {/* Backdrop to close */}
              <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
              <div className="absolute right-0 top-full mt-1 z-50 min-w-[160px] rounded-lg border border-gn-light bg-gn-surface backdrop-blur-sm shadow-xl overflow-hidden">
                {canManageUsers && (
                  <button
                    onClick={() => { setUserMenuOpen(false); navigate('/users'); }}
                    className="flex items-center gap-2 w-full px-3 py-2 text-[11px] text-gn-secondary hover:bg-gn-tertiary hover:text-gn-primary transition-colors"
                  >
                    <Users size={13} />
                    {t('access.title')}
                  </button>
                )}
                {isAdmin && (
                  <button
                    onClick={() => { setUserMenuOpen(false); navigate('/admin'); }}
                    className="flex items-center gap-2 w-full px-3 py-2 text-[11px] text-gn-secondary hover:bg-gn-tertiary hover:text-gn-primary transition-colors"
                  >
                    <Settings size={13} />
                    {t('menu.admin_panel')}
                  </button>
                )}
                <button
                  onClick={() => {
                    const next = isDark ? 'geonexora-light' : 'geonexora-dark';
                    setTheme(next);
                  }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-[11px] text-gn-secondary hover:bg-gn-tertiary hover:text-gn-primary transition-colors"
                >
                  {isDark ? <Sun size={13} /> : <Moon size={13} />}
                  {isDark ? t('menu.light_mode') : t('menu.dark_mode')}
                </button>
                {signsInWithUms && (
                  <button
                    onClick={() => { setUserMenuOpen(false); void changeCompany().then(() => navigate('/login')); }}
                    className="flex items-center gap-2 w-full px-3 py-2 text-[11px] text-gn-secondary hover:bg-gn-tertiary hover:text-gn-primary transition-colors"
                    data-testid="menu-change-company"
                  >
                    <ArrowRightLeft size={13} />
                    {t('staff.change_company')}
                  </button>
                )}
                <div className="border-t border-gn-light" />
                <button
                  onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-[11px] text-gn-secondary hover:bg-gn-danger-light hover:text-gn-danger transition-colors"
                >
                  <LogOut size={13} />
                  {t('menu.logout')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Feature Info Panel */}
      {showInfoPanel && (
        <FeatureInfoPanel
          results={infoResults!}
          onClose={() => setInfoResults(null)}
          onStartCatchment={handleStartCatchment}
          isLoading={catchmentLoading}
        />
      )}

      {/* Catchment Analysis Results Panel */}
      {catchmentResults && catchmentResults.length > 0 && (
        <CatchmentResultPanel
          data={catchmentResults}
          onClose={handleCloseCatchmentResults}
          onClear={handleClearCatchmentResults}
          minimized={resultPanelMinimized}
          onToggleMinimize={() => setResultPanelMinimized((p) => !p)}
        />
      )}

      {/* Report studio: a catchment's results made into a report, or a report opened from a workspace */}
      <ReportStudio />

      {/* Close confirmation dialog for catchment/cannibalization results */}
      {showCloseConfirm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="flex flex-col gap-4 bg-gn-primary border border-gn-warning rounded-xl px-6 py-5 shadow-2xl max-w-sm">
            <div className="flex items-start gap-3">
              <div className="shrink-0 mt-0.5 w-8 h-8 rounded-full bg-gn-warning-light flex items-center justify-center">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gn-warning">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <div>
                <p className="text-gn-primary font-medium text-sm">{t('close_results.title')}</p>
                <p className="text-gn-secondary text-[11px] mt-1.5 leading-relaxed">
                  {t('close_results.body')}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setShowCloseConfirm(false)}
                className="px-3 py-1.5 text-[11px] font-medium rounded border border-gn text-gn-primary hover:bg-gn-tertiary transition-colors"
              >
                {t('buttons.cancel')}
              </button>
              <button
                onClick={() => { setResultPanelMinimized(true); setShowCloseConfirm(false); }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded bg-gn-blue text-gn-inverse hover:bg-gn-blue transition-colors"
              >
                <Minus size={12} />
                {t('report.minimize')}
              </button>
              <button
                onClick={handleConfirmCloseCatchment}
                className="px-3 py-1.5 text-[11px] font-medium rounded bg-gn-danger text-gn-inverse hover:bg-gn-danger transition-colors"
              >
                {t('close_results.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Analysis Panel */}
      {showAnalysisPanel && (
        <AnalysisPanel
          onClose={handleCloseToolPanel}
          selectedPoint={selectedPoint}
          onPointChange={(lonLat) => setSelectedPoint(lonLat)}
          onStartCatchment={handleStartCatchment}
          isLoading={catchmentLoading}
          error={analysisError}
          onDismissError={() => setAnalysisError(null)}
        />
      )}

      {/* Cannibalization Panel */}
      {showCannibalizationPanel && (
        <CannibalizationPanel
          onClose={handleCloseToolPanel}
          point1={cannibPoint1}
          point2={cannibPoint2}
          selectingPoint={cannibSelectingPoint}
          onSetSelecting={setCannibSelectingPoint}
          onPointChange={(which, lonLat) => {
            if (which === 1) setCannibPoint1(lonLat);
            else setCannibPoint2(lonLat);
          }}
          onStartCannibalization={handleStartCannibalization}
          isLoading={catchmentLoading}
          error={analysisError}
          onDismissError={() => setAnalysisError(null)}
        />
      )}

      {/* Distance Panel */}
      {showDistancePanel && (
        <DistancePanel
          onClose={handleCloseToolPanel}
          startPoint={distStartPoint}
          endPoint={distEndPoint}
          selectingPoint={distSelectingPoint}
          onSelectStart={() => setDistSelectingPoint('start')}
          onSelectEnd={() => setDistSelectingPoint('end')}
          onCalculate={handleCalculateDistance}
          results={distResults}
        />
      )}

      {/* Add Point Panel (unified) */}
      {showAddPointPanel && (
        <AddFeaturePanel
          geometryType="Point"
          onClose={handleCloseToolPanel}
          selectedPoint={selectedPoint}
          drawnFeature={addPointFeature}
        />
      )}

      {/* Add Polygon Panel (unified) */}
      {showAddPolygonPanel && (
        <AddFeaturePanel
          geometryType="Polygon"
          onClose={handleCloseToolPanel}
          drawnFeature={addPolygonFeature}
        />
      )}

      {/* Add Line Panel (unified) */}
      {showAddLinePanel && (
        <AddFeaturePanel
          geometryType="LineString"
          onClose={handleCloseToolPanel}
          drawnFeature={addLineFeature}
        />
      )}

      {/* Area Analysis Panel */}
      {showAreaAnalysisPanel && (
        <AreaAnalysisPanel
          onClose={() => {
            // Only clear feature/results — keep draw tool active so user can draw again
            setAreaAnalysisFeature(null);
          }}
          drawnFeature={areaAnalysisFeature}
        />
      )}

      {/* Edit Style Panel */}
      {showEditStylePanel && (
        <EditStylePanel onClose={handleCloseToolPanel} />
      )}

      {/* Street View Panel */}
      {showStreetViewPanel && (
        <StreetViewPanel
          onClose={handleCloseToolPanel}
          selectedPoint={streetViewPoint}
        />
      )}

      {/* Multi Info Panel */}
      {showMultiInfoPanel && (
        <MultiInfoPanel
          onClose={handleCloseToolPanel}
          accumulatedResults={multiInfoResults}
          onClear={() => { setMultiInfoResults([]); clearDispHighlights(); }}
          onStartCatchment={handleStartCatchment}
          isLoading={catchmentLoading}
        />
      )}

      {/* Poly Info Panel */}
      {showPolyInfoPanel && (
        <PolyInfoPanel
          onClose={handleCloseToolPanel}
          clickedResults={polyInfoClickResults}
          clickedFeatureExtent={polyInfoClickExtent}
          onStartCatchment={handleStartCatchment}
          isLoading={catchmentLoading}
        />
      )}

      {/* Filter Panel (admin only) */}
      {showFilterPanel && (
        <FilterPanel onClose={handleCloseToolPanel} />
      )}

      {/* Drawing into a company layer (Workspaces → Edit features) */}
      <EditingHost />

      {/* Legend */}
      {_showLegend && <MapLegend />}

      {/* GeoNexora logo — bottom left, modern watermark style */}
      <div className="fixed bottom-3 left-3 z-10 flex items-center gap-1.5 group cursor-default select-none">
        <div className="flex items-center justify-center w-7 h-7 rounded-full transition-all duration-300"
          style={{
            background: 'linear-gradient(135deg, var(--gn-accent) 0%, #D4551F 100%)',
            boxShadow: '0 2px 8px rgba(232, 98, 42, 0.3)',
          }}
        >
          <svg viewBox="0 0 100 100" width={16} height={16} aria-label="GeoNexora">
            <polygon
              points="50,10 87,30 87,70 50,90 13,70 13,30"
              fill="none"
              stroke="white"
              strokeWidth="6"
              strokeLinejoin="round"
              opacity="0.9"
            />
            <circle cx="50" cy="50" r="8" fill="white" opacity="0.95" />
          </svg>
        </div>
        <span
          className="text-[11px] font-semibold tracking-widest uppercase opacity-50 group-hover:opacity-80 transition-opacity duration-300"
          style={{ color: 'var(--gn-text-secondary)', letterSpacing: '0.12em' }}
        >
          GeoNexora
        </span>
      </div>

      {/* Map Attribution — small, bottom right */}
      <div className="fixed bottom-1 right-2 z-10 text-[9px] text-gn-secondary bg-gn-glass px-1 rounded">
        {t('map.osm_attribution')}
      </div>

      {/* Children / Additional Content */}
      {children && <div className="relative z-20">{children}</div>}
    </div>
  );
};
